import React, { type FunctionComponent, useState } from 'react'
import moment from 'moment'
import { css } from '@emotion/react'
import { SVGIcon, colors } from 'ic-snacks'
import { spacing } from 'gin-and-tonic/foundation/spacing'
import FlexRow from 'legacy/components/FlexRow'
import FlexColumn from 'legacy/components/FlexColumn'
import { type SandboxOrder } from 'domains/connect/components/fulfillment-simulator/utils/types'
import { useDashMessage } from 'intl/intl.hooks'

const callbackLogRow = css`
  border-bottom: 1px solid ${colors.GRAY_88};
  cursor: pointer;
  padding-top: ${spacing.X24};
  padding-bottom: ${spacing.X24};
  padding-left: ${spacing.X12};
  padding-right: ${spacing.X12};
  width: 100%;

  &:hover {
    background: ${colors.GRAY_93};
  }
`
const callbackLogColumn = css`
  align-items: flex-start;
  padding-right: 20px;
`
const callbackLogInfo = css`
  padding: 4px;
  width: 100%;
`
const callbackLogInfoHeader = css`
  font-weight: 600;
  margin-right: ${spacing.X4};
`
const textareaStyle = css`
  margin-top: ${spacing.X12};
  min-height: 400px;
  width: 100%;
`
const callbackToggle = css`
  float: right;
`

const DATETIME_FORMAT = 'MMMM D YYYY, h:mm:ss a z'

interface Props {
  sandboxOrder?: SandboxOrder
}

export const OrderSimulationCallbackLogs: FunctionComponent<Props> = ({ sandboxOrder }) => {
  const [callbackLogsShown, setCallbackLogsShown] = useState({})
  const callbackLogs = sandboxOrder ? sandboxOrder.callbackLogs : []

  const toggleCallbackLogsShown = (callbackId: number) => {
    setCallbackLogsShown({
      ...callbackLogsShown,
      [callbackId]: !callbackLogsShown[callbackId],
    })
  }

  const headers = {
    event: useDashMessage('connect.fulfillmentSimulator.orderProps.callbackLogs.event'),
    status: useDashMessage('connect.fulfillmentSimulator.orderProps.callbackLogs.status'),
    timestamp: useDashMessage('connect.fulfillmentSimulator.orderProps.callbackLogs.timestamp'),
    hideLogs: useDashMessage('connect.fulfillmentSimulator.orderProps.callbackLogs.hideLogs'),
    showLogs: useDashMessage('connect.fulfillmentSimulator.orderProps.callbackLogs.showLogs'),
  }

  const callbackToggleButton = (index: number) => {
    if (callbackLogsShown[index]) {
      return (
        <span css={callbackToggle}>
          {headers.hideLogs}
          <SVGIcon name="arrowUpSmall" size="standard" />
        </span>
      )
    } else {
      return (
        <span css={callbackToggle}>
          {headers.showLogs}
          <SVGIcon name="arrowDownSmall" size="standard" />
        </span>
      )
    }
  }

  const missingRequest = useDashMessage(
    'connect.fulfillmentSimulator.orderProps.callbackLogs.missingRequest'
  )

  return (
    <React.Fragment>
      {callbackLogs.map((callbackLog, index) => (
        <div key={`callbackLog-${index}`}>
          <FlexRow
            css={callbackLogRow}
            key={`callback-${index}`}
            onClick={() => toggleCallbackLogsShown(index)}
          >
            <FlexColumn css={callbackLogColumn}>
              <div css={callbackLogInfo}>
                <span css={callbackLogInfoHeader}>{headers.event}:</span>
                {callbackLog.event}
              </div>
              <div css={callbackLogInfo}>
                <span css={callbackLogInfoHeader}>{headers.status}:</span>
                {(callbackLog.logs[0] && callbackLog.logs[0].responseCode) || missingRequest}
              </div>
              <div css={callbackLogInfo}>
                <span css={callbackLogInfoHeader}>{headers.timestamp}:</span>
                {(callbackLog.logs[0] &&
                  moment.utc(callbackLog.logs[0].timestamp).format(DATETIME_FORMAT)) ||
                  missingRequest}
              </div>
            </FlexColumn>
            <FlexColumn css={callbackLogColumn}>{callbackToggleButton(index)}</FlexColumn>
          </FlexRow>
          <FlexColumn>
            {callbackLogsShown[index] && (
              <textarea
                css={textareaStyle}
                readOnly
                value={JSON.stringify(callbackLog.logs, null, 2)}
              />
            )}
          </FlexColumn>
        </div>
      ))}
    </React.Fragment>
  )
}
